<template>
  <div class="ux-menu" :class="{ right }">
    <span>
      <slot name="activator" :on="on" />
    </span>
    <teleport to="body">
      <transition name="ux-menu-transition" appear>
        <!-- @after-leave="isVisible = false" -->
        <div
          class="ux-menu-outer"
          v-if="isExpanded"
          v-show="isVisible"
          :style="outerStyle"
        >
          <div
            class="ux-menu-panel"
            :class="{ flex }"
            ref="menu"
            :style="innerStyle"
          >
            <slot />
          </div>
        </div>
      </transition>
    </teleport>
  </div>
</template>
<script>
import UXMenuService from "./menu.service.js";

export default {
  props: {
    group: {
      type: String,
    },
    right: {
      type: Boolean,
      default: false,
    },
    bottom: {
      type: Boolean,
      default: false,
    },
    stop: {
      type: Boolean,
      default: false,
    },
    flex: {
      type: Boolean,
      default: false,
    },
    closeOnClick: {
      type: Boolean,
      default: true,
    },
    visible: {
      type: Boolean,
      default: true,
    },
    modelValue: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      outerStyle: {},
      innerStyle: {},
      isExpanded: this.modelValue,
      isVisible: this.visible,
      triggerEvent: null,
      refMenu: null,
      mounted: false,
    };
  },
  watch: {
    modelValue(m) {
      this.expanded = m;
    },
    visible(m) {
      this.isVisible = m;
    },
  },
  mounted() {
    this.refMenu = this.$refs.menu;
    this.mounted = true;
  },
  beforeUnmount() {
    this.removeListeners();
    this.refMenu = null;
    this.mounted = false;

    if (UXMenuService.cache[this.group] === this) {
      UXMenuService.cache[this.group] = null;
    }
  },
  methods: {
    removeListeners() {
      document.removeEventListener("click", this.globalClick);
      if (this.refMenu) {
        this.refMenu.removeEventListener("click", this.immediateClick);
      }
    },
    addEventListeners() {
      document.addEventListener("click", this.globalClick);
      if (this.refMenu) {
        this.refMenu.addEventListener("click", this.immediateClick);
      }
    },
    immediateClick(event) {
      if (this.expanded) {
        if (this.closeOnClick) {
          event.stopImmediatePropagation();
          this.expanded = false;
        }
      }
    },
    globalClick(event) {
      if (this.triggerEvent === event) {
        return;
      }

      var parentElement = this.$refs.menu;
      var childElement = event.srcElement;

      if (parentElement && parentElement.contains(childElement)) {
        //Do nothing
        if (this.closeOnClick) {
          this.expanded = false;
          // event.stopImmediatePropagation();
        }
      } else {
        this.expanded = false;
      }
    },
    position() {
      const rect = this.$el.getBoundingClientRect();

      // Initial positioning
      let left = rect.left + window.scrollX;
      let top = rect.bottom + window.scrollY;

      // Set initial position
      this.outerStyle.left = `${left}px`;
      this.outerStyle.top = `${top}px`;

      // Reset inner positioning
      this.innerStyle = {};

      // Apply right/bottom props if specified
      if (this.right) {
        this.innerStyle.right = `-${rect.width}px`;
      }

      if (this.bottom) {
        this.innerStyle.bottom = `${rect.height}px`;
      }

      var zIndex = this.getMaxZIndex();
      this.outerStyle.zIndex = zIndex;

      // Wait for DOM to render the menu, then check boundaries
      this.$nextTick(() => {
        this.adjustForViewportBoundaries(rect);
      });
    },

    adjustForViewportBoundaries(triggerRect) {
      if (!this.refMenu) return;

      const menuRect = this.refMenu.getBoundingClientRect();
      const viewport = {
        width: window.innerWidth,
        height: window.innerHeight,
      };

      let left = triggerRect.left + window.scrollX;
      let top = triggerRect.bottom + window.scrollY;
      let adjustedPosition = false;

      // Reset inner styles for boundary calculations
      const originalInnerStyle = { ...this.innerStyle };
      this.innerStyle = {};

      // Wait for style reset to take effect
      this.$nextTick(() => {
        const updatedMenuRect = this.refMenu.getBoundingClientRect();

        // Check horizontal boundaries
        const rightOverflow =
          triggerRect.left + updatedMenuRect.width > viewport.width;
        const leftSpace = triggerRect.left;
        const rightSpace = viewport.width - triggerRect.right;

        if (rightOverflow && leftSpace > rightSpace) {
          // Position to the left of trigger
          left = triggerRect.right + window.scrollX - updatedMenuRect.width;
          adjustedPosition = true;
        } else if (this.right && !rightOverflow) {
          // Keep right positioning if it fits
          this.innerStyle.right = `-${triggerRect.width}px`;
        }

        // Check vertical boundaries
        const bottomOverflow =
          triggerRect.bottom + updatedMenuRect.height > viewport.height;
        const topSpace = triggerRect.top;
        const bottomSpace = viewport.height - triggerRect.bottom;

        if (bottomOverflow && topSpace > bottomSpace) {
          // Position above trigger
          top = triggerRect.top + window.scrollY - updatedMenuRect.height;
          adjustedPosition = true;
        } else if (this.bottom && !bottomOverflow) {
          // Keep bottom positioning if it fits
          this.innerStyle.bottom = `${triggerRect.height}px`;
        }

        // Apply final positioning
        if (adjustedPosition) {
          this.outerStyle.left = `${Math.max(0, left)}px`;
          this.outerStyle.top = `${Math.max(0, top)}px`;
        } else {
          // Restore original inner styles if no adjustment needed
          this.innerStyle = originalInnerStyle;
        }

        // Final safety check - ensure menu doesn't go off screen
        this.ensureWithinViewport();
      });
    },

    ensureWithinViewport() {
      if (!this.refMenu) return;

      const menuRect = this.refMenu.getBoundingClientRect();
      const viewport = {
        width: window.innerWidth,
        height: window.innerHeight,
      };

      let currentLeft = parseInt(this.outerStyle.left) || 0;
      let currentTop = parseInt(this.outerStyle.top) || 0;

      // Ensure menu doesn't go beyond right edge
      if (menuRect.right > viewport.width) {
        currentLeft -= menuRect.right - viewport.width + 10; // 10px buffer
      }

      // Ensure menu doesn't go beyond left edge
      if (menuRect.left < 0) {
        currentLeft += Math.abs(menuRect.left) + 10; // 10px buffer
      }

      // Ensure menu doesn't go beyond bottom edge
      if (menuRect.bottom > viewport.height) {
        currentTop -= menuRect.bottom - viewport.height + 10; // 10px buffer
      }

      // Ensure menu doesn't go beyond top edge
      if (menuRect.top < 0) {
        currentTop += Math.abs(menuRect.top) + 10; // 10px buffer
      }

      // Apply corrected position
      this.outerStyle.left = `${Math.max(0, currentLeft)}px`;
      this.outerStyle.top = `${Math.max(0, currentTop)}px`;
    },
    getMaxZIndex() {
      return Math.max(
        ...Array.from(document.querySelectorAll("body *"), (el) =>
          parseFloat(window.getComputedStyle(el).zIndex),
        ).filter((zIndex) => !Number.isNaN(zIndex)),
        0,
      );
    },
    close() {
      this.expanded = false;
    },
  },
  computed: {
    // isInViewport() {
    //     var element = this.$refs.menu;
    //     if(!element) {
    //         return false;
    //     }

    //     const rect = element.getBoundingClientRect();
    //     return (
    //         rect.top >= 0 &&
    //         rect.left >= 0 &&
    //         rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    //         rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    //     );
    // },
    expanded: {
      get() {
        return this.isExpanded;
      },
      set(v) {
        var previousActivatedEntity;

        if (this.group) {
          previousActivatedEntity = UXMenuService.cache[this.group];
        }

        ////////////////////////////////

        if (this.isExpanded != v) {
          this.isExpanded = v;

          //If we're collapsed
          if (v) {
            this.position();
            this.addEventListeners();

            UXMenuService.cache[this.group] = this;

            if (previousActivatedEntity && previousActivatedEntity != this) {
              previousActivatedEntity.expanded = false;
            }

            //////////////////////////////////////////
          } else {
            //Clean up the listeners
            this.removeListeners();
          }
          this.$emit("update:modelValue", v);
        }
      },
    },
    on() {
      var self = this;

      return {
        click(event) {
          if (self.stop) {
            event.stopImmediatePropagation();
          }

          self.triggerEvent = event;
          self.expanded = !self.expanded;
        },
      };
    },
  },
};
</script>
<style lang="scss" scoped>
.ux-menu-outer {
  position: absolute;
  z-index: 10;
  font-size: 12px;
}

.ux-menu-panel {
  position: absolute;
  max-height: 70vh;
  overflow: auto;
  &.flex {
    display: flex;
  }
  border: 1px solid color-mix(in lab, currentColor 10%, transparent);
  box-shadow: 0 0.2em 1em rgba(#000, 0.1);

  :deep(.ux-list-item) {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
}
</style>
